import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Plus, Calendar, Users, Target, MoreVertical, Search /*, Filter*/ } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { GET_PROJECTS } from '../../graphql/queries';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';

export interface Project {
  id: string;
  name: string;
  plan: string;
  deadline: string;
  status: 'planning' | 'active' | 'review' | 'completed';
  progress: number;
  postsTotal: number;
  postsCompleted: number;
  teamMembers: string[];
  color: string;
}

interface ProjectsDashboardProps {
  onCreateProject: () => void;
  onViewProject: (projectId: string) => void;
}

export function ProjectsDashboard({ onCreateProject, onViewProject }: ProjectsDashboardProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { accountId } = useAccount();

  // Fetch projects
  const { data, loading, error } = useQuery(GET_PROJECTS, {
    variables: { accountId },
    skip: !accountId,
  });

  // Transform GraphQL data to component format
  const projects: Project[] =
    data?.projects?.map((p: any) => {
      const postsTotal = p.posts?.length || 0;
      const postsCompleted =
        p.posts?.filter((post: any) => post.status === 'published' || post.status === 'scheduled')
          .length || 0;
      const teamMembers = p.teamMembers?.map((tm: any) => tm.user?.email || '') || [];

      return {
        id: p.id,
        name: p.name,
        plan: p.plan || '',
        deadline: p.deadline,
        status: p.status.toLowerCase() as 'planning' | 'active' | 'review' | 'completed',
        progress: p.progress || 0,
        postsTotal,
        postsCompleted,
        teamMembers,
        color: p.color || 'bg-cmp-bronze',
      };
    }) || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planning':
        return 'bg-cmp-surface text-cmp-ink-2';
      case 'active':
        return 'bg-cmp-surface text-cmp-ink-2 border border-cmp-line';
      case 'review':
        return 'bg-yellow-100 text-yellow-700';
      case 'completed':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-cmp-surface text-cmp-ink-2';
    }
  };

  const getDaysUntilDeadline = (deadline: string) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const filteredProjects = projects.filter((project) => {
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen w-full max-w-full bg-core-dark pt-6 px-8 pb-8 flex items-center justify-center">
        <Loader label="Loading projects" className="text-cmp-ink-3" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full max-w-full bg-core-dark pt-6 px-8 pb-8 flex items-center justify-center">
        <p className="text-red-500">Error loading projects: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full max-w-full bg-core-dark pt-6 px-8 pb-8">
      <div className="max-w-7xl px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="mb-2">Projects</h1>
              <p>Manage your social media projects</p>
            </div>
            <Button onClick={onCreateProject} className="gap-2">
              <Plus className="w-4 h-4" />
              New Project
            </Button>
          </div>

          {/* Filters */}
          <div className="flex gap-4 items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" />
              <Input
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-form-dark"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="planning">Planning</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="review">Review</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-panel border border-cmp-line">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="mb-1">Total Projects</p>
                  <p>{projects.length}</p>
                </div>
                <Target className="w-9 h-9 text-cmp-ink-3" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-panel border border-cmp-line">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="mb-1">Active</p>
                  <p>{projects.filter((p) => p.status === 'active').length}</p>
                </div>
                <div className="w-9 h-9 bg-cmp-bronze-soft rounded-full flex items-center justify-center">
                  <div className="w-[14.4px] h-[14.4px] bg-cmp-bronze rounded-full"></div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-panel border border-cmp-line">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="mb-1">Total Posts</p>
                  <p>{projects.reduce((sum, p) => sum + p.postsTotal, 0)}</p>
                </div>
                <div className="w-9 h-9 bg-cmp-bronze-soft rounded-full flex items-center justify-center">
                  <div className="w-[14.4px] h-[14.4px] bg-cmp-bronze rounded-full"></div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-panel border border-cmp-line">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="mb-1">Team Members</p>
                  <p>{new Set(projects.flatMap((p) => p.teamMembers)).size}</p>
                </div>
                <Users className="w-9 h-9 text-cmp-ink-3" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => {
            const daysLeft = getDaysUntilDeadline(project.deadline);
            return (
              <Card
                key={project.id}
                className="hover:shadow-lg transition-shadow cursor-pointer bg-panel border border-cmp-line"
                onClick={() => onViewProject(project.id)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <div className={`w-1 h-12 ${project.color} rounded-full`}></div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="bg-gray-700 hover:bg-cmp-bronze-soft"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem>Edit</DropdownMenuItem>
                        <DropdownMenuItem>Duplicate</DropdownMenuItem>
                        <DropdownMenuItem className="text-red-600">Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <CardTitle className="mb-2">{project.name}</CardTitle>
                  <CardDescription className="line-clamp-2">{project.plan}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Status & Deadline */}
                    <div className="flex items-center justify-between">
                      <Badge className={getStatusColor(project.status)} variant="secondary">
                        {project.status}
                      </Badge>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {daysLeft > 0 ? `${daysLeft}d left` : `${Math.abs(daysLeft)}d overdue`}
                        </span>
                      </div>
                    </div>

                    {/* Progress */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span>Progress</span>
                        <span>{project.progress}%</span>
                      </div>
                      <Progress value={project.progress} />
                    </div>

                    {/* Posts */}
                    <div className="flex items-center justify-between">
                      <span>Posts</span>
                      <span>
                        {project.postsCompleted} / {project.postsTotal}
                      </span>
                    </div>

                    {/* Team */}
                    <div className="flex items-center justify-between">
                      <span>Team</span>
                      <div className="flex -space-x-2">
                        {project.teamMembers.slice(0, 3).map((member, idx) => (
                          <div
                            key={idx}
                            className="w-10 h-10 rounded-full bg-cmp-bronze-soft text-cmp-bronze-2 border-2 flex items-center justify-center text-sm font-semibold relative flex-shrink-0"
                            style={{
                              zIndex: 10 - idx,
                              borderColor: '#373939',
                            }}
                          >
                            {member.charAt(0).toUpperCase()}
                          </div>
                        ))}
                        {project.teamMembers.length > 3 && (
                          <div
                            className="w-10 h-10 rounded-full bg-cmp-line border-2 flex items-center justify-center text-cmp-ink-2 text-sm font-semibold relative flex-shrink-0"
                            style={{
                              zIndex: 0,
                              borderColor: '#373939',
                            }}
                          >
                            +{project.teamMembers.length - 3}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <p className="text-cmp-ink-3">No projects found matching your criteria</p>
          </div>
        )}
      </div>
    </div>
  );
}
